import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import styles from "./TestSeries.module.css";
import Footer from "./Footer";
import SocialSidebar from "../components/SocialSideBar";
import FAQSection from "../components/FAQSection";

// Image Imports for Key Features
import interfaceImg from "./image/interface.png";
import reportsImg from "./image/reports.png";
import refundImg from "./image/refund.png";
import supportImg from "./image/support.png";
import historyImg from "./image/history.jpg";
import testHomeBg from "./image/testHome.png";
import { jeePremiumSeries, jeeUltimateCombo, neetPremiumSeries, neetUltimatePremium } from "../data/testSeriesData";
import jeeComboImg from "./test-series/images/jee_combo.jpg";
import jeeClass11Img from "./test-series/images/jee_class11.jpg";
import jeeClass12Img from "./test-series/images/jee_class12.jpg";
import jeeFull11Img from "./test-series/images/jee_full11.jpg";
import jeeFull12Img from "./test-series/images/jee_full12.jpg";
import jeeFull1112Img from "./test-series/images/jee_full11_12.jpg";
import jeeAitsImg from "./test-series/images/jee_aits.jpg";
import neetComboImg from "./test-series/images/neet_combo.jpg";
import neetClass11Img from "./test-series/images/neet_class11.jpg";
import neetClass12Img from "./test-series/images/neet_class12.jpg";
import neetFull11Img from "./test-series/images/neet_full11.jpg";
import neetFull12Img from "./test-series/images/neet_full12.jpg";
import neetFull1112Img from "./test-series/images/neet_full11_12.jpg";
import neetAitsImg from "./test-series/images/neet_aits.jpg";

const features = [
  {
    id: "solutions",
    icon: "📈",
    title: "Instant Analysis",
    stat: "Real-time",
    color: "#f59e0b",
  },
  {
    id: "history",
    icon: "🗄️",
    title: "Progress Tracking",
    stat: "Lifetime",
    color: "#ec4899",
  },
];

const keyFeaturesData = [
  {
    key: "exam-interface",
    buttonTitle: "⏱️ Actual Exam like Interface",
    description:
      "",
    imagePlaceholder: interfaceImg,
  },
  {
    key: "detailed-reports",
    buttonTitle: "📈 Instant Performance Report with Detailed Solution",
    description:
      "",
    imagePlaceholder: reportsImg,
  },
  {
    key: "result-history",
    buttonTitle: "🗄️ Centralized Profile Result History",
    description:
      "",
    imagePlaceholder: historyImg,
  },
  {
    key: "doubt-support",
    buttonTitle: "💡 Extra on-demand Doubt Support Sessions",
    description:
      "",
    imagePlaceholder: supportImg,
  },
  {
    key: "return-policy",
    buttonTitle: "💸 15-days No Question-asked Refund Policy",
    description:
      "",
    imagePlaceholder: refundImg,
  },
];

const allSeriesImages = {
  jee_ultimate_combo: jeeComboImg,
  jee_c11_chap: jeeClass11Img,
  jee_c12_chap: jeeClass12Img,
  jee_c11_full: jeeFull11Img,
  jee_c12_full: jeeFull12Img,
  jee_11_12_full: jeeFull1112Img,
  jee_aits: jeeAitsImg,
  neet_ultimate_combo: neetComboImg,
  neet_c11_chap: neetClass11Img,
  neet_c12_chap: neetClass12Img,
  neet_c11_full: neetFull11Img,
  neet_c12_full: neetFull12Img,
  neet_11_12_full: neetFull1112Img,
  neet_aits: neetAitsImg,
};

const premiumScrollCards = [
  ...jeeUltimateCombo.map(s => ({ ...s, type: 'JEE' })),
  ...jeePremiumSeries.map(s => ({ ...s, type: 'JEE' })),
  ...neetUltimatePremium.map(s => ({ ...s, type: 'NEET' })),
  ...neetPremiumSeries.map(s => ({ ...s, type: 'NEET' })),
];

const freeMockCategories = [
  {
    id: 'jee-mains',
    title: 'PYQ Mock Tests (JEE Mains)',
    emoji: '🏆',
    description: 'Practice: JEE Mains Question Papers (2015–2025).',
    testsCount: '38',
    duration: '180 min',
    subjects: 'Physics, Chemistry, Mathematics',
    route: '/test-series/pyq/jeemains',
    accentColor: '#3b82f6',
    examType: 'JEE Mains',
  },
  {
    id: 'neet',
    title: 'PYQ Mock Tests (NEET)',
    emoji: '🧬',
    description: 'Practice: NEET Question Papers (2022–2025).',
    testsCount: '12+',
    duration: '200 min',
    subjects: 'Physics, Chemistry, Biology',
    route: '/test-series/pyq/neet',
    accentColor: '#10b981',
    examType: 'NEET',
  },
  {
    id: 'jee-advanced',
    title: 'PYQ Mock Tests (JEE Adv)',
    emoji: '🎯',
    description: 'Practice: JEE Advanced Question Papers (upcoming more).',
    testsCount: '1+',
    duration: '180 min',
    subjects: 'Physics, Chemistry, Mathematics',
    route: '/test-series/pyq/jeeadv',
    accentColor: '#8b5cf6',
    examType: 'JEE Adv',
  },
];

const formatScrollMeta = (test) => {
  if (test.TestTime === 0) return `${test.NumberOfTests}+ Tests | Full Access`;
  const tests = test.NumberOfTests === 1 ? '1 Test' : `${test.NumberOfTests} Tests`;
  return `${tests} | ${test.TestTime} min each`;
};

const calcScrollSavings = (oldPrice, newPrice) => {
  if (!oldPrice) return 0;
  const oldVal = parseInt(oldPrice.replace(/[^0-9]/g, ''));
  const newVal = parseInt(newPrice.replace(/[^0-9]/g, ''));
  if (!oldVal || !newVal) return 0;
  return Math.round(((oldVal - newVal) / oldVal) * 100);
};

const TestSeries = () => {
  const [showAllLinks, setShowAllLinks] = useState(false);
  const [activeFeature, setActiveFeature] = useState(keyFeaturesData[0].key);
  const [premiumFilter, setPremiumFilter] = useState('all');
  const filteredPremiumCards = premiumFilter === 'all' ? premiumScrollCards : premiumScrollCards.filter(c => c.type === premiumFilter);
  const currentFeatureDetails =
    keyFeaturesData.find((f) => f.key === activeFeature) || keyFeaturesData[0];

  const premiumTrackRef = useRef(null);
  const premiumIsDragging = useRef(false);
  const premiumDragStartX = useRef(0);
  const premiumScrollStartX = useRef(0);

  const freeTrackRef = useRef(null);

  // Premium marquee RAF loop only
  useEffect(() => {
    const speed = 0.7;
    let rafId;
    const animate = () => {
      const pt = premiumTrackRef.current;
      if (pt && !premiumIsDragging.current) {
        pt.scrollLeft += speed;
        if (pt.scrollLeft >= pt.scrollWidth / 2) pt.scrollLeft -= pt.scrollWidth / 2;
      }
      rafId = requestAnimationFrame(animate);
    };
    rafId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafId);
  }, []);

  // Reset premium track scroll position when filter changes
  useEffect(() => {
    if (premiumTrackRef.current) premiumTrackRef.current.scrollLeft = 0;
  }, [premiumFilter]);

  const handlePremiumMouseDown = (e) => {
    premiumIsDragging.current = true;
    premiumDragStartX.current = e.pageX;
    premiumScrollStartX.current = premiumTrackRef.current.scrollLeft;
    premiumTrackRef.current.style.cursor = 'grabbing';
  };
  const handlePremiumMouseMove = (e) => {
    if (!premiumIsDragging.current) return;
    e.preventDefault();
    const dx = e.pageX - premiumDragStartX.current;
    premiumTrackRef.current.scrollLeft = premiumScrollStartX.current - dx;
  };
  const handlePremiumMouseUp = () => {
    premiumIsDragging.current = false;
    if (premiumTrackRef.current) premiumTrackRef.current.style.cursor = 'grab';
  };
  const handlePremiumTouchStart = (e) => {
    premiumIsDragging.current = true;
    premiumDragStartX.current = e.touches[0].pageX;
    premiumScrollStartX.current = premiumTrackRef.current.scrollLeft;
  };
  const handlePremiumTouchMove = (e) => {
    if (!premiumIsDragging.current) return;
    const dx = e.touches[0].pageX - premiumDragStartX.current;
    premiumTrackRef.current.scrollLeft = premiumScrollStartX.current - dx;
  };
  const handlePremiumTouchEnd = () => { premiumIsDragging.current = false; };

  const scrollFreeCards = (dir) => {
    const el = freeTrackRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * 300, behavior: 'smooth' });
  };

  const navigate = useNavigate();

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className={styles.testseriesPage}>
      <SocialSidebar />

      {/* Modern Hero Section */}
      <header className={styles.heroSection} style={{ '--hero-bg': `url(${testHomeBg})` }}>
        <div className={styles.container}>
          <div className={styles.heroBadge}>
            India's Most Authentic & Affordable Test
          </div>
          
          <h1 className={styles.heroTitle}>
            Welcome to <span className={styles.highlightText}> PrrepX Test Portal</span>
          </h1>
          
          <div className={styles.heroFeatures}>
            
            <div className={styles.featureItem}>
              <span className={styles.featureIcon}>📝</span>
              <span className={styles.featureText}>80+ Free Mock Tests</span>
            </div>
            <div className={styles.featureItem}>
              <span className={styles.featureIcon}>📊</span>
              <span className={styles.featureText}>12+ Premium Categories</span>
            </div>

            <div className={styles.featureItem}>
              <span className={styles.featureIcon}>📋</span>
              <span className={styles.featureText}>for JEE/NEET</span>
            </div>
            <div className={styles.featureItem}>
              <span className={styles.featureIcon}>👨‍🏫</span>
              <span className={styles.featureText}>100% By IIT/NIT/Medical Experts</span>
            </div>
          </div>

          <div className={styles.heroActions}>
            <button
              className={styles.primaryBtn}
              onClick={() => scrollToSection("test-categories")}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fbbf24" strokeWidth="2.5">
                <polygon points="5 3 19 12 5 21 5 3"></polygon>
              </svg>
              Explore Test Categories
            </button>
            <button
              className={styles.secondaryBtn}
              onClick={() => navigate("/inquiry-now")}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2">
                <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"></path>
              </svg>
              Talk to Expert
            </button>
          </div>
        </div>
      </header>

      {/* Stats Section */}
      <section className={styles.statsSection}>
        <div className={styles.container}>
          <div className={styles.statsGrid}>
            <div className={styles.statCard}>
              <div className={styles.statNumber}>15 Days</div>
              <div className={styles.statLabel}>Money-Back Guarantee</div>
            </div>
            <div className={styles.statCard}>
              <div className={styles.statNumber}>1000+</div>
              <div className={styles.statLabel}>Test Library</div>
            </div>
            <div className={styles.statCard}>
              <div className={styles.statNumber}>₹199</div>
              <div className={styles.statLabel}>Affordable Pricing</div>
            </div>
            <div className={styles.statCard}>
              <div className={styles.statNumber}>100%</div>
              <div className={styles.statLabel}>Exam Simulation</div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Links Section */}
      <section className={styles.quickLinksSection}>
        <div className={styles.container}>
          <div className={styles.quickLinksWrapper}>
            <span className={styles.quickLinksLabel}>Quick Link:</span>
            <div className={styles.quickLinksGrid}>
              {/* First 4 — always visible on all screens */}
              <Link to="/test-series/jee-premium" className={styles.quickLinkBtn}>JEE Premium Tests</Link>
              <Link to="/test-series/neet-premium" className={styles.quickLinkBtn}>NEET Premium Tests</Link>
              <Link to="/test-series/pyq" className={styles.quickLinkBtn}>Free Mock Tests</Link>
              <Link to="/purchased-tests" className={styles.quickLinkBtn}>My Purchased Tests</Link>

              {/* Remaining links — always visible on desktop, toggled on mobile */}
              <div className={`${styles.quickLinksExtra} ${showAllLinks ? styles.quickLinksExtraOpen : ''}`}>
                <Link to="/profile" className={styles.quickLinkBtn}>My Profile</Link>
                <a href="https://www.prrepxpathshala.live/doubt-portal24*7" target="_blank" rel="noopener noreferrer" className={styles.quickLinkBtn}>Doubt Portal 24×7</a>
                <Link to="/test-series/pyq/jeemains" className={styles.quickLinkBtn}>PYQ Mock Test (JEE Mains)</Link>
                <Link to="/test-series/pyq/neet" className={styles.quickLinkBtn}>PYQ Mock Test (NEET)</Link>
                <Link to="/test-series/pyq/jeeadv" className={styles.quickLinkBtn}>PYQ Mock Test (JEE Adv)</Link>
                <Link to="/inquiry-now" className={styles.quickLinkBtn}>Contact Us</Link>
                <a href="https://prrepxpathshala.live/" target="_blank" rel="noopener noreferrer" className={styles.quickLinkBtn}>Home Portal</a>
              </div>

              {/* Toggle button — mobile only, hidden on desktop via CSS */}
              <button
                onClick={() => setShowAllLinks(!showAllLinks)}
                className={styles.quickLinkBtnViewAll}
              >
                {showAllLinks ? 'View Less ↑' : 'View More ↓'}
              </button>
            </div>
          </div>
        </div>
      </section>

      

      {/* Premium Tests Scroll Section */}
      <section className={styles.premiumScrollSection}>
        <div className={styles.container}>
          <div className={styles.premiumScrollHeader}>
            <h2 className={styles.premiumScrollTitle}>Premium Test Series <span className={styles.premiumScrollCount}>{filteredPremiumCards.length}</span></h2>
            <div className={styles.premiumFilterPills}>
              {['all', 'JEE', 'NEET'].map(f => (
                <button
                  key={f}
                  className={`${styles.premiumFilterPill} ${premiumFilter === f ? styles.premiumFilterPillActive : ''}`}
                  onClick={() => setPremiumFilter(f)}
                >
                  {f === 'all' ? 'All' : f === 'JEE' ? '⚛️ JEE' : '🧬 NEET'}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div
          className={styles.premiumScrollTrack}
          ref={premiumTrackRef}
          onMouseDown={handlePremiumMouseDown}
          onMouseMove={handlePremiumMouseMove}
          onMouseUp={handlePremiumMouseUp}
          onMouseLeave={handlePremiumMouseUp}
          onTouchStart={handlePremiumTouchStart}
          onTouchMove={handlePremiumTouchMove}
          onTouchEnd={handlePremiumTouchEnd}
        >
          <div className={styles.premiumScrollInner}>
            {[...filteredPremiumCards, ...filteredPremiumCards].map((card, i) => {
              const isCombo = card.Contains_ids && card.Contains_ids.length > 0;
              return (
                <div key={i} className={styles.premiumScrollCard} data-is-combo={isCombo}>
                  <div className={styles.pscCardHeader}>
                    <span className={styles.pscTestBadge}>📚 TEST SERIES</span>
                    <span className={`${styles.pscTypeBadge} ${card.type === 'JEE' ? styles.pscTypeJee : styles.pscTypeNeet}`}>
                      {card.type === 'JEE' ? '⚛️ JEE' : '🧬 NEET'}
                    </span>
                  </div>
                  <div className={styles.pscCardBody}>
                    <h3 className={styles.pscTestTitle}>{card.title}</h3>
                    <div className={styles.pscTestMeta}>{formatScrollMeta(card)}</div>
                    {allSeriesImages[card.id] && (
                      <div className={styles.pscTestImageWrapper}>
                        <img src={allSeriesImages[card.id]} alt={card.title} className={styles.pscTestImage} loading="lazy" />
                      </div>
                    )}
                    <div className={styles.pscPricingCompact}>
                      <div className={styles.pscPriceLine}>
                        <span className={styles.pscPriceLabel}>Price:</span>
                        {card.oldPrice && <span className={styles.pscOldPriceCompact}>{card.oldPrice}</span>}
                        <span className={styles.pscPriceCompact}>{card.price.startsWith('₹') ? card.price : `₹${card.price}`}</span>
                        {card.oldPrice && (
                          <span className={styles.pscDiscountCompact}>
                            <span className={styles.pscDiscountAnim}>{calcScrollSavings(card.oldPrice, card.price)}% OFF</span>
                          </span>
                        )}
                      </div>
                      <div className={styles.pscValidityLine}>
                        <span className={styles.pscValidityLabel}>Validity:</span>
                        <span className={styles.pscValidityValue}>{card.ValidityYears}Year</span>
                      </div>
                    </div>
                    <button className={styles.pscBtnExplore} onClick={() => navigate(card.link)}>Explore Now</button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Free Mock Tests Scroll Section */}
      <section className={styles.freeScrollSection}>
        <div className={styles.container}>
          <div className={styles.freeScrollHeader}>
            <div>
              <h2 className={styles.freeScrollTitle}>
                Free Mock Tests <span className={styles.freeScrollCount}>{freeMockCategories.length} Categories</span>
              </h2>
              <p className={styles.freeScrollSubtitle}>🎁 100% Free Forever • Practice actual PYQ papers as mock tests</p>
            </div>
            <div className={styles.freeArrowBtns}>
              <button className={styles.freeArrowBtn} onClick={() => scrollFreeCards(-1)} aria-label="Scroll left">&#8592;</button>
              <button className={styles.freeArrowBtn} onClick={() => scrollFreeCards(1)} aria-label="Scroll right">&#8594;</button>
            </div>
          </div>
          <div className={styles.freeScrollTrack} ref={freeTrackRef}>
            <div className={styles.freeScrollInner}>
              {freeMockCategories.map((cat, i) => (
                <div key={i} className={styles.freeScrollCard}>
                  <div className={styles.fscCardAccentBar} style={{ background: `linear-gradient(90deg, ${cat.accentColor}, #1e3a8a)` }} />
                  <div className={styles.fscCardBody}>
                    <div className={styles.fscCardHeader}>
                      <span className={styles.fscEmoji}>{cat.emoji}</span>
                      <span className={styles.fscFreeBadge}>🎁 100% FREE</span>
                    </div>
                    <span className={styles.fscExamBadge} style={{ background: `${cat.accentColor}18`, color: cat.accentColor }}>{cat.examType}</span>
                    <h3 className={styles.fscTestTitle}>{cat.title}</h3>
                    <p className={styles.fscDescription}>{cat.description}</p>
                    <div className={styles.fscCardStats}>
                      <div className={styles.fscStatItem}>
                        <span className={styles.fscStatLabel}>Tests</span>
                        <span className={styles.fscStatValue} style={{ color: cat.accentColor }}>{cat.testsCount}</span>
                      </div>
                      <div className={styles.fscStatItem}>
                        <span className={styles.fscStatLabel}>Duration</span>
                        <span className={styles.fscStatValue} style={{ color: cat.accentColor }}>{cat.duration}</span>
                      </div>
                    </div>
                    <div className={styles.fscSubjects}>
                      <span className={styles.fscSubjectsIcon}>📖</span>
                      <span className={styles.fscSubjectsText}>{cat.subjects}</span>
                    </div>
                    <div className={styles.fscCardFooter}>
                      <button className={styles.fscBtnTake} onClick={() => navigate(cat.route)}>
                        Start Practicing →
                      </button>
                    </div>
                  </div>
                </div>
            ))}
            </div>
          </div>
        </div>
      </section>

      {/* Key Features Section */}
      <section className={styles.keyFeaturesSection}>
        <div className={styles.keyFeatureContentWrapper}>
          <div className={styles.keyFeatureButtonsArea}>
            <h2 className={styles.keyFeatureSectionTitle}>Key Features</h2>
            {keyFeaturesData.map((feature) => {
              const isActive = activeFeature === feature.key;
              return (
                <React.Fragment key={feature.key}>
                  <button
                    className={`${styles.keyFeatureBtn} ${
                      isActive ? styles.activeKeyFeatureBtn : ""
                    }`}
                    onClick={() => setActiveFeature(isActive ? null : feature.key)}
                  >
                    {feature.buttonTitle}
                    <span className={`${styles.keyFeatureChevron} ${isActive ? styles.keyFeatureChevronOpen : ""}`}></span>
                  </button>
                  <div className={`${styles.keyFeatureInlinePanel} ${isActive ? styles.keyFeatureInlinePanelActive : ""}`}>
                    <div className={styles.keyFeatureDetailBox}>
                      <h3 className={styles.keyFeatureDetailTitle}>{feature.buttonTitle}</h3>
                      <p
                        className={styles.keyFeatureDetailDesc}
                        dangerouslySetInnerHTML={{ __html: feature.description }}
                      ></p>
                      <div className={styles.keyFeatureDetailImagePlaceholder}>
                        <img
                          src={feature.imagePlaceholder}
                          alt={feature.buttonTitle}
                          className={styles.keyFeatureImage}
                        />
                      </div>
                    </div>
                  </div>
                </React.Fragment>
              );
            })}
          </div>

          <div className={styles.keyFeatureDetailsArea}>
            <div className={styles.keyFeatureDetailBox}>
              <h3 className={styles.keyFeatureDetailTitle}>
                {currentFeatureDetails.buttonTitle}
              </h3>
              <p
                className={styles.keyFeatureDetailDesc}
                dangerouslySetInnerHTML={{
                  __html: currentFeatureDetails.description,
                }}
              ></p>
              <div className={styles.keyFeatureDetailImagePlaceholder}>
                <img
                  src={currentFeatureDetails.imagePlaceholder}
                  alt={currentFeatureDetails.buttonTitle}
                  className={styles.keyFeatureImage}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Section - Redesigned for Mobile */}
      <section className={styles.compareSection}>
        <div className={styles.container}>
          <h2 className={styles.compareHeading}>Compare with Other Platforms</h2>
          <div className={styles.tableWrapper}>
            <table className={styles.compareTable}>
              <thead>
                <tr>
                  <th>Features</th>
                  <th className={styles.brandCol}>PrrepX Pathshala</th>
                  <th>Other Platforms</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>All India Level Test Series</td>
                  <td>✅ Yes</td>
                  <td>✅ Yes</td>
                </tr>
                <tr>
                  <td>Chapterwise Tests (11th & 12th)</td>
                  <td>✅ Yes</td>
                  <td>✅ Yes (Few)</td>
                </tr>
                <tr>
                  <td>Full Length Tests</td>
                  <td>✅ Yes</td>
                  <td>✅ Yes (Few)</td>
                </tr>
                <tr>
                  <td>Previous Year Test Series (Free)</td>
                  <td>
                    <span className={styles.check}>✅ Yes</span>
                    <p className={styles.tableSmallText}>Around 80 previous year test series for JEE/NEET. Actual papers are the most authentic mock tests.</p>
                  </td>
                  <td>❌ No</td>
                </tr>
                <tr>
                  <td>Instant Result</td>
                  <td>✅ Yes (Immediate analysis with detailed Solutions)</td>
                  <td>❌ No (Mostly next day)</td>
                </tr>
                <tr>
                  <td>Real Exam Interface</td>
                  <td>✅ Yes (Replicates similar interface)</td>
                  <td>⚠️ Very few (Partial)</td>
                </tr>
                <tr>
                  <td>Affordable Pricing</td>
                  <td className={styles.priceHighlight}>💰 ₹199 only (Best in market)</td>
                  <td>💸 ₹3k–₹5k nearly</td>
                </tr>
                <tr>
                  <td>Refund Policy</td>
                  <td>✅ 15-day No Question-asked</td>
                  <td>❌ No refund available</td>
                </tr>
                <tr>
                  <td>Question Quality</td>
                  <td>✅ Created by Only IIT/NIT/Doctor mentors</td>
                  <td>⚠️ Very few</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section id="test-categories" className={styles.categoriesSection}>
        <div className={styles.container}>
          <h2 className={styles.sectionTitle}>Explore Test Categories</h2>
          <div className={styles.categoryGrid}>

            <Link to="/test-series/pyq" className={styles.categoryCard}>
              <div className={styles.catIcon}>🎁</div>
              <h3>PYQ Tests (Free)</h3>
              <p>Practice with actual past papers for JEE & NEET.</p>
              <span className={styles.viewLink}>View All Tests →</span>
            </Link>

            <Link to="/test-series/jee-premium" className={styles.categoryCard}>
              <div className={styles.catIcon}>🚀</div>
              <h3>JEE Premium</h3>
              <p>Chapter-wise, full-length, and All-India mock tests.</p>
              <span className={styles.viewLink}>View All Tests →</span>
            </Link>

            <Link to="/test-series/neet-premium" className={styles.categoryCard}>
              <div className={styles.catIcon}>🩺</div>
              <h3>NEET Premium</h3>
              <p>Specialized tests to excel in medical entrance exams.</p>
              <span className={styles.viewLink}>View All Tests →</span>
            </Link>
          </div>
        </div>
      </section>

      <FAQSection
        title="Frequently Asked Questions"
        faqs={[
          {
            question: "What is included in the PrrepX Pathshala Test Series?",
            answer: "You get access to chapter-wise, full-length, and previous year tests for JEE & NEET, instant results, detailed solutions, and a real exam interface."
          },
          {
            question: "Are the tests based on the latest exam pattern?",
            answer: "Yes, all tests are curated by IIT/NIT/Doctor mentors and strictly follow the latest NTA exam pattern for JEE and NEET."
          },
          {
            question: "Is there a refund policy?",
            answer: "Absolutely! We offer a 15-day, no-questions-asked refund policy on all premium test series purchases."
          },
          {
            question: "How do I access my purchased tests?",
            answer: "Once purchased, all your tests are available in your profile dashboard under 'Purchased Tests'."
          },
          {
            question: "Can I practice previous year papers for free?",
            answer: "Yes! We provide around 80 previous year test series for JEE/NEET absolutely free."
          },
          {
            question: "Are solutions provided for every question?",
            answer: "Yes, detailed step-by-step solutions are available for every question immediately after you finish the test."
          },
          {
            question: "Is my progress saved?",
            answer: "All your test results and progress are saved in your account for easy tracking and review."
          }
        ]}
      />

      <Footer />
    </div>
  );
};

export default TestSeries;